<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" size="lg" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Add to Organizational Unit</div>
        </template>

        <div class="flex flex-col gap-4">
            <vx-alert v-if="props.user.id" :closeable="false" shadow>
                Selected user <strong class="font-bold">{{ props.user.email }}</strong>
            </vx-alert>

            <team-user-org-unit-assignment v-model="assignedOrgUnits" ref="orgUnitAssignmentRef" />
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click.prevent="updateVisible(false)"
                    type="button"
                    data-test="cancel-button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click.prevent="submit"
                    data-test="add-button"
                    :loading="submitting"
                    :disabled="assignedOrgUnits.length === 0"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Add <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxModal } from '@voxie/frontend-components';
import { orgUnitUsersClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import TeamUserOrgUnitAssignment from './TeamUserOrgUnitAssignment.vue';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';
import { HTTP_CREATED } from '~/support/HttpStatuses';

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    user: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['updatedOrgUnitUser']);

const toasted = useToasted();
const submitting = ref(false);

const orgUnitAssignmentRef = ref(null);
const assignedOrgUnits = ref([]);

const updateVisible = (isVisible) => {
    orgUnitAssignmentRef.value?.reset();
    visible.value = isVisible;
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    let orgUnitsAdded = 0;

    try {
        for (const orgUnit of assignedOrgUnits.value) {
            try {
                const response = await orgUnitUsersClient.upsertOrgUnitUser(props.teamId, orgUnit.id, props.user.id, {
                    role: orgUnit.role,
                });

                if (response.status === HTTP_CREATED) {
                    orgUnitsAdded++;
                }
            } catch (orgUnitError) {
                const message = `Failed to invite user to organizational unit ${orgUnit.name}:`;
                toasted.error(`${message}: ${catchRequestError(orgUnitError)}`);
            }
        }

        emit('updatedOrgUnitUser', {
            user_id: props.user.id,
            org_units_added: orgUnitsAdded,
        });

        toasted.success('User updated successfully')

        orgUnitAssignmentRef.value?.reset();

        updateVisible(false);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
